import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import type { RelatedSupplyChainEntry } from '@/types/story'

/**
 * Story-detail callout listing supply-chain provider rollups linked to a
 * story via its `primary_vendors` UUID array (Plan C Plan 2 T21 / spec
 * §5.5). The data is enriched server-side (T10) and arrives on the
 * `getStoryDetail` response as `story.relatedSupplyChain`.
 *
 * Self-hides when the array is empty / undefined — most stories outside
 * the AWS / Azure / GCP / Okta / Auth0 / OneLogin orbit will not surface
 * this callout. Each entry links to `/providers/:slug`.
 */
export function StorySupplyChainCallout({
  entries,
}: {
  entries: readonly RelatedSupplyChainEntry[] | undefined
}) {
  if (!entries || entries.length === 0) return null

  return (
    <Card aria-labelledby="story-supply-chain-callout-title">
      <CardHeader>
        <CardTitle
          id="story-supply-chain-callout-title"
          className="text-base"
        >
          Supply-chain context
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {entries.map((e) => (
          <div
            key={e.providerSlug}
            className="flex items-center justify-between gap-3 rounded-md border bg-card p-3"
          >
            <div className="space-y-0.5">
              <Link
                to={`/providers/${e.providerSlug}`}
                className="text-sm font-semibold hover:underline"
              >
                {e.providerName}
              </Link>
              <div className="text-xs text-muted-foreground">
                {e.recentIncidentCount} incident
                {e.recentIncidentCount === 1 ? '' : 's'} (last 90d)
                {e.lastCriticalAt && (
                  <>
                    {' · last critical '}
                    {format(new Date(e.lastCriticalAt), 'yyyy-MM-dd')}
                  </>
                )}
              </div>
            </div>
            <Link
              to={`/providers/${e.providerSlug}`}
              className="text-sm text-primary hover:underline"
            >
              View →
            </Link>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
